import React, { useState } from "react";
import "./assessmentQuestionsCard.scss";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IconButtons from "../IconButtons/IconButtons";
import {
  black_plus_default_icon,
  clone_active_icon,
  clone_disabled_icon,
  delete_active_icon,
  delete_disabled_icon,
  down_arrow_icon,
  edit_active_icon,
  edit_disabled_icon,
  plus_active_icon,
  plus_disabled_icon,
  up_arrow_icon,
} from "../../constants/icons";
import { Box } from "@mui/system";
import NoDataAvailable from "../NoDataAvailable/NoDataAvailable";
import { font_10, font_13, font_13_icon } from "../../utils/utils";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
import AssessmentOptionModal from "../AssessmentOptionModal/assessmentOptionModal";

const getCanViewEditOption = (
  canViewEditOption,
  optionEditModal,
  option,
  id,
  canEditOption,
  editOptionMessage
) => {
  return (
    <>
      {canViewEditOption && (
        <IconButtons
          tooltip="Edit Option"
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          handleClick={() => optionEditModal(option, id)}
          classList="margin-right-1"
          disabled={!canEditOption}
          disabledImage={edit_disabled_icon}
          disabledTooltip={!canEditOption ? editOptionMessage : null}
        />
      )}
    </>
  );
};

const getCanViewDeleteOption = (
  canViewDeleteOption,
  deleteModalOpenOption,
  option,
  id,
  canDeleteOption,
  deleteOptionMessage
) => {
  return (
    <>
      {canViewDeleteOption && (
        <IconButtons
          tooltip="Delete Option"
          width={font_13_icon}
          height={font_13_icon}
          image={delete_active_icon}
          handleClick={(event) => deleteModalOpenOption(event, option)}
          id={id}
          disabled={!canDeleteOption}
          disabledImage={delete_disabled_icon}
          disabledTooltip={!canDeleteOption ? deleteOptionMessage : null}
        />
      )}
    </>
  );
};

// Helper function to get toggle tooltip title
const getToggleTooltipTitle = (
  canToggleActiveQuestion,
  toggleActiveQuestionMessage,
  isActive
) => {
  if (!canToggleActiveQuestion) {
    return toggleActiveQuestionMessage;
  }
  return isActive ? "Inactivate Question" : "Activate Question";
};

// Component for Question Header Actions
const QuestionHeaderActions = ({
  canViewToggleActiveQuestion,
  canToggleActiveQuestion,
  toggleActiveQuestionMessage,
  question,
  toggleQuestionModalOpen,
  handleSwitchClick,
  canViewEditQuestion,
  canEditQuestion,
  editQuestionMessage,
  questionEditModal,
  id,
  canViewCloneQuestion,
  canCloneQuestion,
  cloneQuestionMessage,
  cloneQuestionModalOpen,
  canViewDeleteQuestion,
  canDeleteQuestion,
  deleteQuestionMessage,
  deleteModalOpenQuestion,
  expanded,
}) => {
  const toggleTitle = getToggleTooltipTitle(
    canToggleActiveQuestion,
    toggleActiveQuestionMessage,
    question?.active
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {canViewToggleActiveQuestion && (
        <BootstrapTooltip title={toggleTitle}>
          <Box>
            <Switch
              disabled={!canToggleActiveQuestion}
              className="margin-right-2"
              checked={question?.active || false}
              size="small"
              onChange={(event) =>
                toggleQuestionModalOpen(event, question?.active, question?.id)
              }
              onClick={(event) => handleSwitchClick(event)}
              sx={{
                margin: "0 0.5rem",
                "& .MuiSwitch-thumb": {
                  width: 11,
                  height: 11,
                },
              }}
            />
          </Box>
        </BootstrapTooltip>
      )}
      {canViewEditQuestion && (
        <IconButtons
          tooltip="Edit Question"
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          handleClick={(event) => questionEditModal(event, id, question)}
          classList="margin-right-1"
          disabled={!canEditQuestion}
          disabledImage={edit_disabled_icon}
          disabledTooltip={!canEditQuestion ? editQuestionMessage : null}
        />
      )}
      {canViewCloneQuestion && (
        <IconButtons
          tooltip="Clone Question"
          width={font_13_icon}
          height={font_13_icon}
          image={clone_active_icon}
          handleClick={(event) => cloneQuestionModalOpen(event, question)}
          id={id}
          classList="margin-right-1"
          disabledImage={clone_disabled_icon}
          disabled={!canCloneQuestion}
          disabledTooltip={!canCloneQuestion ? cloneQuestionMessage : null}
        />
      )}
      {canViewDeleteQuestion && (
        <IconButtons
          tooltip="Delete Question"
          width={font_13_icon}
          height={font_13_icon}
          image={delete_active_icon}
          handleClick={(event) => deleteModalOpenQuestion(event)}
          id={id}
          classList="margin-right-1"
          disabled={!canDeleteQuestion}
          disabledImage={delete_disabled_icon}
          disabledTooltip={!canDeleteQuestion ? deleteQuestionMessage : null}
        />
      )}
      <IconButtons
        height={font_13}
        width={font_13}
        image={expanded ? up_arrow_icon : down_arrow_icon}
      />
    </Box>
  );
};

// Component for Question Chips
const QuestionChips = ({ question, difficultyLevels, questionTypes }) => {
  const difficultyLevel = difficultyLevels?.find(
    (level) => level?.key === question?.difficultyLevel
  );
  const questionType = questionTypes?.find(
    (level) => level?.key === question?.type
  );

  return (
    <Grid className="related-chip">
      <Box className="related-chip-info related-chip-left">
        <Box className="level-chip">
          <Typography variant="font_12" align="left">
            <Chip
              className="same-size-chip"
              label={difficultyLevel?.value}
              variant={difficultyLevel?.key}
            />
          </Typography>
        </Box>
        <Box className="type-chip">
          <Typography variant="font_12" align="left">
            <Chip
              className="same-size-type-chip"
              label={questionType?.value}
              variant="QUEST_TYPE"
            />
          </Typography>
        </Box>
      </Box>
      <Box className="related-chip-info related-chip-right">
        <Box className="more-info">
          <Typography variant="font_12" align="left">
            <Chip
              className="more-info-chip"
              label={question?.createdBy?.fullName}
              variant="QUEST_TYPE"
            />
          </Typography>
        </Box>
        <Box className="more-info">
          <Typography variant="font_12" align="left">
            <Chip
              className="more-info-chip"
              label={dayjs(question?.dateCreated)?.format("DD/MM/YYYY hh:mm A")}
              variant="QUEST_TYPE"
            />
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

// Component for Options Table Row
const OptionTableRow = ({
  option,
  optionId,
  optionModalOpen,
  deleteModalOpen,
  optionEditModal,
  deleteModalOpenOption,
  id,
  question,
  setOptionModalOpen,
  setOptionId,
  questionOptions,
}) => {
  const optionPermission = option?.permission;
  const {
    canViewEditOption,
    canEditOption,
    canViewDeleteOption,
    canDeleteOption,
    deleteOptionMessage,
    editOptionMessage,
  } = optionPermission;

  const isEditing =
    optionId === option?.id && optionModalOpen && !deleteModalOpen;

  if (isEditing) {
    return (
      <TableRow className="table-body-row" key={option?.id}>
        <AssessmentOptionModal
          setOptionModalOpen={setOptionModalOpen}
          questionId={question?.id}
          optionId={optionId}
          option={option}
          setOptionId={setOptionId}
          questionOptions={questionOptions}
        />
      </TableRow>
    );
  }

  return (
    <TableRow className="table-body-row" key={option?.id}>
      <TableCell align="left" className="padding-left-1">
        <Typography
          variant="font_12"
          className={option?.deleted ? "room-deleted" : null}
          sx={{ wordBreak: "break-word" }}
        >
          {option?.optionText}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="font_12"
          className={option.deleted ? "room-deleted" : null}
        >
          <Checkbox
            color="secondary"
            size="small"
            checked={option?.correct}
            disabled
          />
        </Typography>
      </TableCell>
      <TableCell align="center">
        {getCanViewEditOption(
          canViewEditOption,
          optionEditModal,
          option,
          id,
          canEditOption,
          editOptionMessage
        )}
        {getCanViewDeleteOption(
          canViewDeleteOption,
          deleteModalOpenOption,
          option,
          id,
          canDeleteOption,
          deleteOptionMessage
        )}
      </TableCell>
    </TableRow>
  );
};

// Component for Options Table
const OptionsTable = ({
  canViewOptionsList,
  questionOptions,
  addNewOption,
  questionId,
  question,
  optionId,
  optionModalOpen,
  deleteModalOpen,
  optionEditModal,
  deleteModalOpenOption,
  id,
  setOptionModalOpen,
  setOptionId,
  setAddNewOption,
}) => {
  const hasOptions =
    questionOptions?.totalResults ||
    (addNewOption && questionId === question?.id);

  if (!canViewOptionsList || !hasOptions) {
    if (question?.type !== "TEXT") {
      return (
        <NoDataAvailable
          imgHeight={4}
          imgWidth={4.5}
          message="No Options Exist"
        />
      );
    }
    return null;
  }

  return (
    <TableContainer className="table-container">
      <Table stickyHeader>
        <TableHead className="table-head-container">
          <TableRow className="table-head-row">
            <TableCell
              align="left"
              className="padding-left-1"
              sx={{ minWidth: "18rem", maxWidth: "18rem" }}
            >
              <Typography variant="font_13_bold">Option</Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: "8rem" }}>
              <Typography variant="font_13_bold">Correct</Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: "8rem" }}>
              <Typography variant="font_13_bold">Actions</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {questionOptions?.results
            ?.filter((option) => !option?.deleted)
            ?.map((option) => (
              <OptionTableRow
                key={option?.id}
                option={option}
                optionId={optionId}
                optionModalOpen={optionModalOpen}
                deleteModalOpen={deleteModalOpen}
                optionEditModal={optionEditModal}
                deleteModalOpenOption={deleteModalOpenOption}
                id={id}
                question={question}
                setOptionModalOpen={setOptionModalOpen}
                setOptionId={setOptionId}
                questionOptions={questionOptions}
              />
            ))}
          {!optionId &&
            addNewOption &&
            questionId === question?.id &&
            optionModalOpen && (
              <TableRow className="table-body-row">
                <AssessmentOptionModal
                  setOptionModalOpen={setOptionModalOpen}
                  questionId={question?.id}
                  optionId={optionId}
                  setOptionId={setOptionId}
                  questionOptions={questionOptions}
                  setAddNewOption={setAddNewOption}
                  addNewOption={addNewOption}
                />
              </TableRow>
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AssessmentQuestionsCard = ({
  questionEditModal,
  deleteModalOpenQuestion,
  cloneQuestionModalOpen,
  id,
  optionEditModal,
  handleOptionModalOpen,
  deleteModalOpenOption,
  question,
  toggleQuestionModalOpen,
  handleSwitchClick,
  optionId,
  setOptionId,
  optionModalOpen,
  setOptionModalOpen,
  questionId,
  addNewOption,
  setAddNewOption,
  deleteModalOpen,
}) => {
  const permission = question?.permission;
  const questionTypes = useSelector(
    (state) => state?.utility?.constants?.questionTypes
  );
  const difficultyLevels = useSelector(
    (state) => state?.utility?.constants?.difficultyLevels
  );
  const {
    canViewAddOptions,
    canViewCloneQuestion,
    canDeleteQuestion,
    canEditQuestion,
    canViewToggleActiveQuestion,
    canViewDeleteQuestion,
    canViewEditQuestion,
    editQuestionMessage,
    deleteQuestionMessage,
    canToggleActiveQuestion,
    canAddOptions,
    canViewOptionsList,
    canCloneQuestion,
    toggleActiveQuestionMessage,
    addOptionsMessage,
    cloneQuestionMessage,
  } = permission;
  const questionOptions = question?.questionOptions;
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <Accordion className="accordion" key={id} onChange={handleAccordionChange}>
      <AccordionSummary
        className="assessment-accordion-summary"
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Box className="assessment-questions-head-container">
          <Box className="assessment-questions-head" sx={{ flexBasis: "83%" }}>
            <Typography
              variant="font_14"
              sx={{ textAlign: "justify", whiteSpace: "pre-line" }}
            >
              {question?.title}
            </Typography>
          </Box>
          <Box
            className="assessment-questions-head-right"
            sx={{ flexBasis: "17%" }}
          >
            <QuestionHeaderActions
              canViewToggleActiveQuestion={canViewToggleActiveQuestion}
              canToggleActiveQuestion={canToggleActiveQuestion}
              toggleActiveQuestionMessage={toggleActiveQuestionMessage}
              question={question}
              toggleQuestionModalOpen={toggleQuestionModalOpen}
              handleSwitchClick={handleSwitchClick}
              canViewEditQuestion={canViewEditQuestion}
              canEditQuestion={canEditQuestion}
              editQuestionMessage={editQuestionMessage}
              questionEditModal={questionEditModal}
              id={id}
              canViewCloneQuestion={canViewCloneQuestion}
              canCloneQuestion={canCloneQuestion}
              cloneQuestionMessage={cloneQuestionMessage}
              cloneQuestionModalOpen={cloneQuestionModalOpen}
              canViewDeleteQuestion={canViewDeleteQuestion}
              canDeleteQuestion={canDeleteQuestion}
              deleteQuestionMessage={deleteQuestionMessage}
              deleteModalOpenQuestion={deleteModalOpenQuestion}
              expanded={expanded}
            />
          </Box>
        </Box>
        <QuestionChips
          question={question}
          difficultyLevels={difficultyLevels}
          questionTypes={questionTypes}
        />
      </AccordionSummary>

      <AccordionDetails className="accordion-details">
        <Box className="margin-bottom-5">
          <Typography variant="font_13_bold">Description</Typography>
          <Grid container className="assessment-questions-container">
            <Grid item md={12} className="bmv-content">
              <Box className="assessment-info-box">
                <Typography
                  variant="font_13"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {question?.description ? question?.description : "-"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <OptionsTable
          canViewOptionsList={canViewOptionsList}
          questionOptions={questionOptions}
          addNewOption={addNewOption}
          questionId={questionId}
          question={question}
          optionId={optionId}
          optionModalOpen={optionModalOpen}
          deleteModalOpen={deleteModalOpen}
          optionEditModal={optionEditModal}
          deleteModalOpenOption={deleteModalOpenOption}
          id={id}
          setOptionModalOpen={setOptionModalOpen}
          setOptionId={setOptionId}
          setAddNewOption={setAddNewOption}
        />
        {canViewAddOptions &&
          (question?.type === "MULTI_SELECT" ||
            question?.type === "SINGLE_SELECT") && (
            <BootstrapTooltip title={!canAddOptions ? addOptionsMessage : null}>
              <Box>
                <Button
                  sx={{ marginTop: "1rem" }}
                  fullWidth
                  className="block-dashed-btn assessment-questions-dashed-btn"
                  color="secondary"
                  image={black_plus_default_icon}
                  onClick={() => handleOptionModalOpen(id)}
                  startIcon={
                    <IconButtons
                      width={font_10}
                      height={font_10}
                      image={plus_active_icon}
                      disabledImage={plus_disabled_icon}
                      disabled={!canAddOptions}
                    />
                  }
                  disabled={!canAddOptions}
                >
                  <Typography variant="font_11_bold">Add Option</Typography>
                </Button>
              </Box>
            </BootstrapTooltip>
          )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AssessmentQuestionsCard;
